/**
 * Icon Registry - Maps icon names to Tabler React components
 * Icons organized by category from Figma design system
 */

import {
	// Sidebar
	IconLayoutDashboard,
	IconPhotoVideo,
	IconCloudUpload,
	IconUpload,
	IconFileExport,

	// Application-specific
	IconMovie,
	IconCarouselHorizontal,
	IconClick,
	IconMessageCircle,
	IconSparkles,

	// Position
	IconLayersDifference,
	IconLayoutDistributeVertical,
	IconLayoutSidebar,
	IconLayoutSidebarLeftExpand,

	// Operations and controllers
	IconSlash,
	IconBan,
	IconGripVertical,

	// Devices / Recording
	IconDeviceComputerCamera,
	IconDeviceDesktop,
	IconMicrophone,

	// Other
	IconVolume,
	IconVolume3,
	IconCloud,
	IconDragDrop,
	IconAlarm,

	// Caret (filled)
	IconCaretDownFilled,
	IconCaretUpDownFilled,
	IconCaretUpFilled,
	IconCaretRightFilled,
	IconCaretLeftFilled,

	// Align
	IconLayoutAlignLeft,
	IconLayoutAlignRight,
	IconLayoutAlignTop,
	IconLayoutAlignMiddle,
	IconLayoutAlignBottom,
	IconLayoutAlignCenter,

	// Arrange
	IconStack2,
	IconStackFront,
	IconStackForward,
	IconStackBack,
	IconStackBackward,
	IconBackground,
	IconLetterXSmall,
	IconLetterYSmall,
	IconArrowAutofitWidth,
	IconArrowAutofitHeight,
	IconAspectRatio,

	// Transform
	IconTransform,
	IconFlipHorizontal,
	IconFlipVertical,
	IconArrowsMaximize,
	IconMaximize,
	IconMinimize,
	IconArrowBarBoth,
	IconAngle,
	IconRotateClockwise2,
	IconRotate2,

	// Edit
	IconPlus,
	IconMinus,
	IconCopy,
	IconClipboard,
	IconCopyPlus,
	IconTrash,
	IconArrowBackUp,
	IconArrowForwardUp,
	IconScissors,
	IconBrandSpeedtest,
	IconBrandVimeoFilled,
	IconBrandYoutubeFilled,
	IconCrop,
	IconSquareRoundedX,

	// Object
	IconLockOpen,
	IconLock,
	IconEyeClosed,
	IconEye,
	IconCrown,
	IconCrownFilled,
	IconVideo,
	IconVideoPlus,
	IconMusic,
	IconPhoto,
	IconCircleSquare,
	IconTextSize,
	IconSquareHalf,
	IconCheck,
	IconCircleCheckFilled,
	IconCircleCheck,
	IconCircleXFilled,

	// Appearance
	IconDroplet,
	IconColorSwatch,
	IconBulb,
	IconSunHigh,
	IconRipple,
	IconRadiusBottomLeft,
	IconRadiusBottomRight,
	IconRadiusTopLeft,
	IconRadiusTopRight,
	IconWand,

	// Playback
	IconPlayerPlayFilled,
	IconPlayerPlay,
	IconPlayerStopFilled,
	IconPlayerStop,
	IconPlayerPauseFilled,
	IconPlayerPause,
	IconPlayerRecordFilled,
	IconPlayerRecord,
	IconPlayerSkipBackFilled,
	IconPlayerSkipBack,
	IconPlayerSkipForwardFilled,
	IconPlayerSkipForward,
	IconPlayerTrackPrevFilled,
	IconPlayerTrackPrev,
	IconPlayerTrackNextFilled,
	IconPlayerTrackNext,

	// Directional
	IconArrowUp,
	IconArrowDown,
	IconArrowLeft,
	IconArrowRight,
	IconChevronUp,
	IconChevronDown,
	IconChevronLeft,
	IconChevronRight,

	// Interface
	IconSearch,
	IconAdjustmentsHorizontal,
	IconX,
	IconDots,
	IconInfoCircle,
	IconAlertCircle,
	IconPlaceholder,
	IconArrowAutofitContent,
	IconRefresh,
	IconLayoutGrid,
	IconArrowsSort,

	// Interactions
	IconListDetails,
	IconSquareCheck,
	IconInputCheck,
	IconChartBar,
	IconMessages,
	IconCode,
	IconFileTypePdf,
	IconKeyframes,
	IconTimeline,
	IconCloudCheck,
	IconLogout,
	IconSum,
	IconBold,
	IconItalic,
	IconStrikethrough,
	IconUnderline,
	IconList,
	IconListNumbers,

	// Added in Figma iconography sync
	IconAlertTriangle,
	IconAlignBoxCenterStretch,
	IconAlignBoxLeftStretch,
	IconAlignBoxRightStretch,
	IconAlignCenter,
	IconAlignJustified,
	IconAlignLeft,
	IconAlignRight,
	IconArrowDownLeft,
	IconArrowDownRight,
	IconArrowUpLeft,
	IconArrowUpRight,
	IconBadgeAd,
	IconBadgeAdFilled,
	IconBadgeCc,
	IconBadgeCcFilled,
	IconBell,
	IconChalkboardTeacher,
	IconCircleX,
	IconCloudExclamation,
	IconColorPicker,
	IconColumnInsertLeft,
	IconColumnInsertRight,
	IconDiamonds,
	IconDiamondsFilled,
	IconDropletOff,
	IconFileTextSpark,
	IconHelpCircle,
	IconHistory,
	IconLayoutSidebarLeftCollapse,
	IconLineHeight,
	IconLinkOff,
	IconLoader3,
	IconOmega,
	IconRewindBackward10,
	IconRewindForward10,
	IconRotateClockwise,
	IconRowInsertBottom,
	IconRowInsertTop,
	IconSettings2,
	IconShare2,
	IconTable,
	IconTextPlus,
	IconUnlink,

	// Added in latest Figma iconography sync (node 12-339)
	IconChevronsUp,
	IconChevronsDown,
	IconChevronsLeft,
	IconChevronsRight,
	IconDownload,
	IconExternalLink,
	IconHandFinger,
	IconHelpCircleFilled,
	IconLink,
	IconLoader2,
	IconPencil,
	IconTemplate,
} from "@tabler/icons-react";

import type { Icon as TablerIcon } from "@tabler/icons-react";
import { memo } from "react";

import { OBSOLETE_ICON_REGISTRY, type ObsoleteIconName } from "../obsolete/IconObsolete";
import { ColorWheel, DeviceDesktopAndCamera, MousePointerText } from "./IconCustom";
import { CaptionsOffLegacyIcon } from "../obsolete/IconObsoleteLegacy";

/**
 * Icon registry mapping icon names to Tabler components
 */
export const ICON_REGISTRY = {
	// Sidebar
	"layout-dashboard": IconLayoutDashboard,
	"photo-video": IconPhotoVideo,
	"cloud-upload": IconCloudUpload,
	upload: IconUpload,
	"file-export": IconFileExport,
	"captions-off": CaptionsOffLegacyIcon,

	// Application-specific
	movie: IconMovie,
	"carousel-horizontal": IconCarouselHorizontal,
	click: IconClick,
	"message-circle": IconMessageCircle,
	sparkles: IconSparkles,

	// Position
	"layers-difference": IconLayersDifference,
	"layout-distribute-vertical": IconLayoutDistributeVertical,
	"layout-sidebar": IconLayoutSidebar,
	"layout-sidebar-left-expand": IconLayoutSidebarLeftExpand,

	// Operations and controllers
	slash: IconSlash,
	ban: IconBan,
	"grip-vertical": IconGripVertical,

	// Devices / Recording
	"device-computer-camera": IconDeviceComputerCamera,
	"device-desktop-screen": IconDeviceDesktop,
	"device-desktop-camera-and-screen": DeviceDesktopAndCamera,
	microphone: IconMicrophone,

	// Other
	diamonds: IconDiamonds,
	"diamonds-filled": IconDiamondsFilled,
	volume: IconVolume,
	"volume-3": IconVolume3,
	"cloud-check": IconCloudCheck,
	"cloud": IconCloud,
	"drag-drop": IconDragDrop,
	alarm: IconAlarm,
	logout: IconLogout,

	// Caret (filled)
	"caret-up-down": IconCaretUpDownFilled,

	// Align
	"layout-align-left": IconLayoutAlignLeft,
	"layout-align-right": IconLayoutAlignRight,
	"layout-align-top": IconLayoutAlignTop,
	"layout-align-middle": IconLayoutAlignMiddle,
	"layout-align-bottom": IconLayoutAlignBottom,
	"layout-align-center": IconLayoutAlignCenter,

	// Arrange
	"stack-2": IconStack2,
	"stack-front": IconStackFront,
	"stack-forward": IconStackForward,
	"stack-back": IconStackBack,
	"stack-backward": IconStackBackward,
	background: IconBackground,
	"letter-x-small": IconLetterXSmall,
	"letter-y-small": IconLetterYSmall,
	"arrow-autofit-width": IconArrowAutofitWidth,
	"arrow-autofit-height": IconArrowAutofitHeight,
	"aspect-ratio": IconAspectRatio,

	// Transform
	transform: IconTransform,
	// Tabler names flips by axis; these mean the direction the clip moves.
	"flip-horizontal": IconFlipVertical,
	"flip-vertical": IconFlipHorizontal,
	"arrows-maximize": IconArrowsMaximize,
	maximize: IconMaximize,
	minimize: IconMinimize,
	"arrow-bar-both": IconArrowBarBoth,
	angle: IconAngle,
	"rotate-clockwise-2": IconRotateClockwise2,
	"rotate-2": IconRotate2,

	// Edit
	plus: IconPlus,
	minus: IconMinus,
	copy: IconCopy,
	clipboard: IconClipboard,
	"copy-plus": IconCopyPlus,
	trash: IconTrash,
	"arrow-back-up": IconArrowBackUp,
	"arrow-forward-up": IconArrowForwardUp,
	scissors: IconScissors,
	"brand-speedtest": IconBrandSpeedtest,
	"brand-youtube": IconBrandYoutubeFilled,
	"brand-vimeo": IconBrandVimeoFilled,
	crop: IconCrop,
	"square-rounded-x": IconSquareRoundedX,
	"keyframes": IconKeyframes,
	"timeline": IconTimeline,

	// Object
	"lock-open": IconLockOpen,
	lock: IconLock,
	"eye-closed": IconEyeClosed,
	eye: IconEye,
	crown: IconCrown,
	"crown-filled": IconCrownFilled,
	video: IconVideo,
	"video-plus": IconVideoPlus,
	music: IconMusic,
	photo: IconPhoto,
	"circle-square": IconCircleSquare,
	"text-size": IconTextSize,
	"square-half": IconSquareHalf,
	check: IconCheck,
	"circle-check-filled": IconCircleCheckFilled,
	"circle-check": IconCircleCheck,
	"circle-x-filled": IconCircleXFilled,

	// Appearance
	droplet: IconDroplet,
	"color-swatch": IconColorSwatch,
	bulb: IconBulb,
	"sun-high": IconSunHigh,
	ripple: IconRipple,
	"radius-bottom-left": IconRadiusBottomLeft,
	"radius-bottom-right": IconRadiusBottomRight,
	"radius-top-left": IconRadiusTopLeft,
	"radius-top-right": IconRadiusTopRight,
	wand: IconWand,

	// Playback
	"player-play-filled": IconPlayerPlayFilled,
	"player-play": IconPlayerPlay,
	"player-stop-filled": IconPlayerStopFilled,
	"player-stop": IconPlayerStop,
	"player-pause-filled": IconPlayerPauseFilled,
	"player-pause": IconPlayerPause,
	"player-record-filled": IconPlayerRecordFilled,
	"player-record": IconPlayerRecord,
	"player-skip-back-filled": IconPlayerSkipBackFilled,
	"player-skip-back": IconPlayerSkipBack,
	"player-skip-forward-filled": IconPlayerSkipForwardFilled,
	"player-skip-forward": IconPlayerSkipForward,
	"player-track-prev-filled": IconPlayerTrackPrevFilled,
	"player-track-prev": IconPlayerTrackPrev,
	"player-track-next-filled": IconPlayerTrackNextFilled,
	"player-track-next": IconPlayerTrackNext,

	// Directional
	"arrow-up": IconArrowUp,
	"arrow-down": IconArrowDown,
	"arrow-left": IconArrowLeft,
	"arrow-right": IconArrowRight,
	"chevron-up": IconChevronUp,
	"chevron-down": IconChevronDown,
	"chevron-left": IconChevronLeft,
	"chevron-right": IconChevronRight,

	// Interface
	search: IconSearch,
	"adjustments-horizontal": IconAdjustmentsHorizontal,
	x: IconX,
	dots: IconDots,
	"info-circle": IconInfoCircle,
	"alert-circle": IconAlertCircle,
	placeholder: IconPlaceholder,
	"arrow-autofit-content": IconArrowAutofitContent,
	refresh: IconRefresh,
	"layout-grid": IconLayoutGrid,
	"arrows-sort": IconArrowsSort,

	// Interactions
	"list-details": IconListDetails,
	"square-check": IconSquareCheck,
	"input-check": IconInputCheck,
	"chart-bar": IconChartBar,
	messages: IconMessages,
	code: IconCode,
	"file-type-pdf": IconFileTypePdf,
	pause: IconPlayerPause,
	// Same glyph as the "stack-2" arrange icon; Figma reuses it for the interactions-group badge.
	"interactions-group": IconStack2,
	sum: IconSum,
	bold: IconBold,
	italic: IconItalic,
	strikethrough: IconStrikethrough,
	underline: IconUnderline,
	list: IconList,
	"list-numbers": IconListNumbers,

	// Added in Figma iconography sync
	"alert-triangle": IconAlertTriangle,
	"align-box-center-stretch": IconAlignBoxCenterStretch,
	"align-box-left-stretch": IconAlignBoxLeftStretch,
	"align-box-right-stretch": IconAlignBoxRightStretch,
	"align-center": IconAlignCenter,
	"align-justified": IconAlignJustified,
	"align-left": IconAlignLeft,
	"align-right": IconAlignRight,
	"arrow-down-left": IconArrowDownLeft,
	"arrow-down-right": IconArrowDownRight,
	"arrow-up-left": IconArrowUpLeft,
	"arrow-up-right": IconArrowUpRight,
	"badge-ad-filled": IconBadgeAdFilled,
	"badge-ad-outline": IconBadgeAd,
	"badge-cc-filled": IconBadgeCcFilled,
	"badge-cc-outline": IconBadgeCc,
	bell: IconBell,
	"caret-down": IconCaretDownFilled,
	"caret-left": IconCaretLeftFilled,
	"caret-right": IconCaretRightFilled,
	"caret-up": IconCaretUpFilled,
	"chalkboard-teacher": IconChalkboardTeacher,
	"circle-x": IconCircleX,
	"cloud-exclamation": IconCloudExclamation,
	"color-picker": IconColorPicker,
	"column-insert-left": IconColumnInsertLeft,
	"column-insert-right": IconColumnInsertRight,
	"diamonds-outline": IconDiamonds,
	"droplet-off": IconDropletOff,
	"file-text-spark": IconFileTextSpark,
	"help-circle": IconHelpCircle,
	history: IconHistory,
	"layout-sidebar-left-collapse": IconLayoutSidebarLeftCollapse,
	"line-height": IconLineHeight,
	"link-off": IconLinkOff,
	"loader-3": IconLoader3,
	omega: IconOmega,
	"rewind-backward-10": IconRewindBackward10,
	"rewind-forward-10": IconRewindForward10,
	"rotate-clockwise": IconRotateClockwise,
	"row-insert-bottom": IconRowInsertBottom,
	"row-insert-top": IconRowInsertTop,
	"settings-2": IconSettings2,
	"share-2": IconShare2,
	table: IconTable,
	"text-plus": IconTextPlus,
	unlink: IconUnlink,

	// Added in latest Figma iconography sync (node 12-339)
	link: IconLink,
	"external-link": IconExternalLink,
	pencil: IconPencil,
	download: IconDownload,
	template: IconTemplate,
	"loader-2": IconLoader2,
	"help-circle-filled": IconHelpCircleFilled,
	"chevrons-up": IconChevronsUp,
	"chevrons-down": IconChevronsDown,
	"chevrons-left": IconChevronsLeft,
	"chevrons-right": IconChevronsRight,
	"color-wheel": ColorWheel,
	"free-response": IconTextSize,
	"mouse-pointer-hand": IconHandFinger,
	"mouse-pointer-text": MousePointerText,
} as const;

export type IconName = keyof typeof ICON_REGISTRY;
export type TablerIconComponent = TablerIcon;

/** Curated names plus obsolete-bucket names (pending design review, WBC3-2001). */
export type AnyIconName = IconName | ObsoleteIconName;

/** Merged lookup — curated wins on collision; the intersection test in Icon.test.ts keeps overlap at zero. Exported for UikitIconObsolete (the legacy Icon element-API shim). */
export const MERGED_ICON_REGISTRY = { ...OBSOLETE_ICON_REGISTRY, ...ICON_REGISTRY };

/**
 * Icon categories for documentation/storybook
 */
export const ICON_CATEGORIES = {
	sidebar: ["layout-dashboard", "photo-video", "cloud-upload", "file-export", "captions-off"],
	"application-specific": ["movie", "carousel-horizontal", "message-circle"],
	position: ["layers-difference", "layout-sidebar"],
	other: ["drag-drop", "alarm"],
	"mouse-pointers": ["mouse-pointer-hand", "mouse-pointer-text"],
	align: ["layout-align-left", "layout-align-right", "layout-align-top", "layout-align-middle", "layout-align-bottom", "layout-align-center", "align-box-left-stretch", "align-box-center-stretch", "align-box-right-stretch", "align-left", "align-center", "align-right", "align-justified"],
	arrange: ["stack-2", "stack-front", "stack-forward", "stack-back", "stack-backward", "background", "letter-x-small", "letter-y-small", "arrow-autofit-width", "arrow-autofit-height", "aspect-ratio", "grip-vertical"],
	transform: ["transform", "flip-horizontal", "flip-vertical", "arrows-maximize", "arrow-bar-both", "angle", "rotate-clockwise", "rotate-clockwise-2", "rotate-2"],
	edit: ["plus", "minus", "copy", "clipboard", "copy-plus", "trash", "arrow-back-up", "arrow-forward-up", "scissors", "brand-speedtest", "crop", "square-rounded-x", "list", "list-numbers", "layout-grid", "table", "link", "link-off", "unlink", "external-link", "omega", "sum", "keyframes", "timeline", "layout-sidebar-left-expand", "layout-sidebar-left-collapse", "pencil", "sparkles", "row-insert-top", "row-insert-bottom", "column-insert-left", "column-insert-right"],
	object: ["lock-open", "lock", "cloud", "cloud-check", "cloud-exclamation", "eye-closed", "eye", "crown", "crown-filled", "video", "video-plus", "music", "photo", "circle-square", "text-size", "square-half", "check", "circle-check-filled", "circle-check", "diamonds-filled", "diamonds-outline"],
	appearance: ["droplet", "droplet-off", "color-swatch", "bulb", "sun-high", "ripple", "radius-bottom-left", "radius-bottom-right", "radius-top-left", "radius-top-right", "wand", "bold", "italic", "strikethrough", "underline", "line-height", "text-plus", "color-wheel", "color-picker", "microphone", "file-text-spark", "chalkboard-teacher"],
	playback: ["player-play-filled", "player-play", "player-stop-filled", "player-stop", "player-pause-filled", "player-pause", "player-record-filled", "player-record", "player-skip-back-filled", "player-skip-back", "player-skip-forward-filled", "player-skip-forward", "player-track-prev-filled", "player-track-prev", "player-track-next-filled", "player-track-next", "badge-cc-outline", "badge-cc-filled", "badge-ad-outline", "badge-ad-filled", "rewind-backward-10", "rewind-forward-10", "volume", "volume-3"],
	directional: ["arrow-up", "arrow-down", "arrow-left", "arrow-right", "arrow-up-left", "arrow-up-right", "arrow-down-left", "arrow-down-right", "chevron-up", "chevron-down", "chevron-left", "chevron-right", "chevrons-up", "chevrons-down", "chevrons-right", "chevrons-left", "caret-up", "caret-down", "caret-left", "caret-right", "caret-up-down", "arrows-sort"],
	interface: ["search", "adjustments-horizontal", "settings-2", "x", "circle-x", "circle-x-filled", "dots", "info-circle", "help-circle", "help-circle-filled", "placeholder", "arrow-autofit-content", "upload", "download", "share-2", "maximize", "minimize", "alert-triangle", "alert-circle", "bell", "history", "template", "logout", "slash", "refresh", "loader-2", "loader-3", "device-computer-camera", "device-desktop-screen", "device-desktop-camera-and-screen"],
	interactions: ["click", "list-details", "square-check", "input-check", "free-response", "chart-bar", "messages", "pause", "code", "file-type-pdf", "interactions-group"],
} as const satisfies Record<string, readonly IconName[]>;

export type IconCategory = keyof typeof ICON_CATEGORIES;

/**
 * Visual stroke width mapping from Figma design system (in pixels at rendered size)
 */
export const SIZE_STROKE_MAP: Record<IconSize, number> = {
	12: 1.0,
	16: 1.25,
	20: 1.5,
	24: 1.75,
	28: 2.0,
	32: 2.25,
	36: 2.5,
	40: 2.75,
	44: 3.0,
	48: 3.25,
};

/**
 * Tabler icons use a 24x24 viewBox, so stroke-width scales with the icon.
 * To achieve the desired visual stroke width, we divide by the scale factor.
 */
const BASE_ICON_SIZE = 24;
// Exported for UikitIconObsolete, which renders raw registry glyphs itself.
export const getStrokeWidth = (size: IconSize): number => {
	const scale = size / BASE_ICON_SIZE;
	return SIZE_STROKE_MAP[size] / scale;
};

/**
 * Default aria-label mapping from Figma design system
 * Maps icon names to their human-readable labels
 */
export const ICON_LABELS: Record<IconName, string> = {
	// Sidebar
	"layout-dashboard": "Dashboard",
	"photo-video": "Photo video",
	"cloud-upload": "Cloud upload",
	upload: "Upload",
	"file-export": "File export",
	"captions-off": "Captions",

	// Application-specific
	movie: "Movie",
	"carousel-horizontal": "Carousel",
	click: "Interactions",
	"message-circle": "Message",
	sparkles: "AI - generate, suggest, auto-enhance",

	// Position
	"layers-difference": "Layers difference",
	"layout-sidebar": "Sidebar layout",
	"layout-sidebar-left-expand": "Track headers expanded",

	// Operations
	slash: "Slash",
	ban: "Ban",
	"grip-vertical": "Grip",

	// Devices / Recording
	"device-computer-camera": "Camera",
	"device-desktop-screen": "Screen",
	"device-desktop-camera-and-screen": "Camera and screen",
	microphone: "Voiceover",

	// Other
	volume: "Volume 100%",
	"volume-3": "Volume 0% or Muted",
	diamonds: "Transition",
	cloud: "Cloud",
	"drag-drop": "Drag and drop",
	alarm: "Alarm",
	"cloud-check": "Cloud synced",
	logout: "Exit",

	// Caret
	"caret-up-down": "Caret up and down",

	// Align
	"layout-align-left": "Align to left",
	"layout-align-right": "Align to right",
	"layout-align-top": "Align to top",
	"layout-align-middle": "Align to middle",
	"layout-align-bottom": "Align to bottom",
	"layout-align-center": "Align to center",

	// Arrange
	"stack-2": "Arrange General",
	"stack-front": "Bring to front",
	"stack-forward": "Bring forward",
	"stack-back": "Send to back",
	"stack-backward": "Send backward",
	background: "Set as background",
	"letter-x-small": "X position",
	"letter-y-small": "Y position",
	"arrow-autofit-width": "Width",
	"arrow-autofit-height": "Height",
	"aspect-ratio": "Aspect ratio",

	// Transform
	transform: "Transform",
	"flip-horizontal": "Flip horizontally",
	"flip-vertical": "Flip vertically",
	"arrows-maximize": "Fill",
	maximize: "Maximize",
	minimize: "Minimize",
	"arrow-bar-both": "Fit",
	angle: "Angle",
	"rotate-clockwise-2": "Rotate 90° clockwise",
	"rotate-2": "Rotate 90° counterclockwise",

	// Edit
	plus: "Plus or Add",
	minus: "Minus or Subtract",
	copy: "Copy",
	clipboard: "Paste",
	"copy-plus": "Duplicate",
	trash: "Delete",
	"arrow-back-up": "Undo",
	"arrow-forward-up": "Redo",
	scissors: "Split",
	"layout-distribute-vertical": "Trim",
	"brand-speedtest": "Speed",
	"brand-youtube": "YouTube",
	"brand-vimeo": "Vimeo",
	crop: "Crop",
	"square-rounded-x": "Clear",
	"keyframes": "Keyframes",
	"timeline": "Timeline",

	// Object
	"lock-open": "Unlock",
	lock: "Lock",
	"eye-closed": "Visibility hide",
	eye: "Visibility show",
	crown: "Premium content outline",
	"crown-filled": "Premium content filled",
	video: "Video",
	"video-plus": "Video external sources",
	music: "Audio",
	photo: "Image",
	"circle-square": "Shape",
	"text-size": "Text",
	"square-half": "Transition",
	check: "Check",
	"circle-check-filled": "Check circle filled",
	"circle-check": "Check circle outline",
	"circle-x-filled": "Close filled",


	// Appearance
	droplet: "Opacity 100%",
	"color-swatch": "Color",
	bulb: "White balance",
	"sun-high": "Light",
	ripple: "Texture",
	"radius-bottom-left": "Corner radius bottom left",
	"radius-bottom-right": "Corner radius bottom right",
	"radius-top-left": "Corner radius top left",
	"radius-top-right": "Corner radius top right",
	wand: "Auto adjust",

	// Playback
	"player-play-filled": "Play filled",
	"player-play": "Play outline",
	"player-stop-filled": "Stop filled",
	"player-stop": "Stop outline",
	"player-pause-filled": "Pause filled",
	"player-pause": "Pause outline",
	"player-record-filled": "Record filled",
	"player-record": "Record outline",
	"player-skip-back-filled": "Skip back filled",
	"player-skip-back": "Skip back outline",
	"player-skip-forward-filled": "Skip forward filled",
	"player-skip-forward": "Skip forward outline",
	"player-track-prev-filled": "Rewind filled",
	"player-track-prev": "Rewind outline",
	"player-track-next-filled": "Fast-forward filled",
	"player-track-next": "Fast-forward outline",

	// Directional
	"arrow-up": "Arrow up",
	"arrow-down": "Arrow down",
	"arrow-left": "Arrow left",
	"arrow-right": "Arrow right",
	"chevron-up": "Chevron up",
	"chevron-down": "Chevron down",
	"chevron-left": "Chevron left",
	"chevron-right": "Chevron right",

	// Interface
	search: "Search",
	"adjustments-horizontal": "Settings or Adjustments",
	x: "Close",
	dots: "More",
	"info-circle": "Info",
	"alert-circle": "Warning circle",
	placeholder: "Placeholder",
	"arrow-autofit-content": "Fit to view",
	refresh: "Refresh",
	"layout-grid": "Grid view",
	"arrows-sort": "Sort",

	// Interactions
	"list-details": "Multiple choice",
	"square-check": "Check all that apply",
	"input-check": "Fill in the blank",
	"chart-bar": "Poll",
	messages: "Discussion",
	code: "Web embed",
	"file-type-pdf": "PDF file",
	pause: "Pause",
	"interactions-group": "Interactions group",
	sum: "Equation",
	bold: "Bold",
	italic: "Italic",
	strikethrough: "Strikethrough",
	underline: "Underline",
	list: "Bullet list",
	"list-numbers": "Number list",

	// Added in Figma iconography sync
	"alert-triangle": "Warning triangle",
	"align-box-center-stretch": "Box align to center",
	"align-box-left-stretch": "Box align to left",
	"align-box-right-stretch": "Box align to right",
	"align-center": "Text align to center",
	"align-justified": "Text align justified",
	"align-left": "Text align to left",
	"align-right": "Text align to right",
	"arrow-down-left": "Arrow down left",
	"arrow-down-right": "Arrow down right",
	"arrow-up-left": "Arrow up left",
	"arrow-up-right": "Arrow up right",
	"badge-ad-filled": "Audio descriptions filled",
	"badge-ad-outline": "Audio descriptions outline",
	"badge-cc-filled": "Closed captions filled",
	"badge-cc-outline": "Closed captions outline",
	bell: "Notifications",
	"caret-down": "Caret down",
	"caret-left": "Caret left",
	"caret-right": "Caret right",
	"caret-up": "Caret up",
	"chalkboard-teacher": "Chalkboard",
	"circle-x": "Close circle",
	"cloud-exclamation": "Cloud not synced",
	"color-picker": "Color picker",
	"column-insert-left": "Insert left",
	"column-insert-right": "Insert right",
	"diamonds-filled": "Transition fill",
	"diamonds-outline": "Transition outline",
	"droplet-off": "Opacity 0%",
	"file-text-spark": "Text to speech",
	"help-circle": "Help",
	history: "Version history",
	"layout-sidebar-left-collapse": "Track headers collapsed",
	"line-height": "Line height",
	"link-off": "Link broken",
	"loader-3": "Loading and progress",
	omega: "Special characters",
	"rewind-backward-10": "Backward 10 seconds",
	"rewind-forward-10": "Forward 10 seconds",
	"rotate-clockwise": "Rotate",
	"row-insert-bottom": "Insert bottom",
	"row-insert-top": "Insert top",
	"settings-2": "Effects",
	"share-2": "Share",
	table: "Table",
	"text-plus": "Text settings",
	unlink: "Unlink",
	link: "Link",
	"external-link": "External link",
	pencil: "Edit, rename",
	download: "Download",
	template: "Template",
	"loader-2": "Loading",
	"help-circle-filled": "Help filled",
	"chevrons-up": "Chevrons up",
	"chevrons-down": "Chevrons down",
	"chevrons-left": "Chevrons left",
	"chevrons-right": "Chevrons right",
	"color-wheel": "Color wheel",
	"free-response": "Free response",
	"mouse-pointer-hand": "Hand pointer",
	"mouse-pointer-text": "Text cursor",
};

export type IconSize = 12 | 16 | 20 | 24 | 28 | 32 | 36 | 40 | 44 | 48;

export interface IconProps {
	/** Icon name from the curated or obsolete registry */
	name: AnyIconName;
	/** Icon size in pixels (default: 24) */
	size?: IconSize;
	/** Icon color (default: currentColor) */
	color?: string;
	/** Additional CSS class */
	className?: string;
	/** Override stroke width (uses size-based default if not specified) */
	strokeWidth?: number;
	/** Accessibility label (defaults to icon's display name from design system) */
	"aria-label"?: string;
	/** Set to true to hide from screen readers (overrides aria-label) */
	"aria-hidden"?: boolean;
	// The legacy `@uikit` icons carried a `dataCy` prop that e2e selects on, and this
	// component has no rest spread, so the hook has to be declared and forwarded
	// explicitly — TypeScript cannot catch its absence, because hyphenated JSX attributes
	// are exempt from excess-property checking (same reasoning as Toggle/ButtonUtility).
	"data-cy"?: string;
}

/**
 * Icon component that renders icons with design system sizing
 *
 * Automatically applies the correct stroke width based on size from Figma specs:
 * - 12px → 1.00px stroke
 * - 16px → 1.25px stroke
 * - 20px → 1.50px stroke
 * - 24px → 1.75px stroke
 * - 28px → 2.00px stroke
 * - 32px → 2.25px stroke
 * - 36px → 2.50px stroke
 * - 40px → 2.75px stroke
 * - 44px → 3.00px stroke
 * - 48px → 3.25px stroke
 *
 * All icons have aria-label set by default from the design system.
 * Use aria-hidden={true} to hide decorative icons from screen readers.
 */
const warnedObsoleteNames = new Set<string>();

const humanizeIconName = (name: string): string => {
	const label = name.replace(/-/g, " ");
	return label.charAt(0).toUpperCase() + label.slice(1);
};

export const Icon = memo(function Icon({
	name,
	size = 24,
	color = "currentColor",
	className,
	strokeWidth,
	"aria-label": ariaLabel,
	"aria-hidden": ariaHidden,
	"data-cy": dataCy,
}: IconProps) {
	const IconComponent = MERGED_ICON_REGISTRY[name];

	if (!IconComponent) {
		return null;
	}

	if (import.meta.env.DEV && !(name in ICON_REGISTRY) && !warnedObsoleteNames.has(name)) {
		warnedObsoleteNames.add(name);
		// eslint-disable-next-line no-console -- uikit3 cannot depend on @core/logger; dev-only visibility for WBC3-2001
		console.warn(`[uikit3] Icon "${name}" is in the obsolete registry — pending design review (WBC3-2001).`);
	}

	const computedStrokeWidth = strokeWidth ?? getStrokeWidth(size);
	const computedAriaLabel = ariaLabel ?? (name in ICON_LABELS ? ICON_LABELS[name as IconName] : humanizeIconName(name));

	return (
		<IconComponent
			size={size}
			color={color}
			stroke={computedStrokeWidth}
			className={className}
			aria-label={ariaHidden ? undefined : computedAriaLabel}
			aria-hidden={ariaHidden}
			data-cy={dataCy}
		/>
	);
});
